import cn from 'classnames'
import dynamic from 'next/dynamic'
import React, { memo, useEffect } from 'react'
import { useSelector } from 'react-redux'
// import { ArticleCart } from 'src/components/ArticleCart/ArticleCart'
// import { CompareModalContent } from 'src/components/CompareModalContent/CompareModalContent'
import Drawer from 'src/components/global/Drawer/Drawer'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { RelatedContents } from 'src/components/Partials/RelatedContents/RelatedContents'
import SkeletonLoading from 'src/components/SkeletonLoading/SkeletonLoading'
// import { Section } from 'src/components/global/Overview/Section'
// import { SimilarSolutions } from 'src/components/SimilarSolutions/SimilarSolutions'
import { SolutionFounderItem } from 'src/components/SolutionFounderItem/SolutionFounderItem'
import { THEME } from 'src/config/config'
import {
  FOUNDERS_EXECUTIVE_TEAM,
  FUNDING_DETAILS,
  fundingAmountsC,
  fundingInfosC,
  OPEN_TO_INVESTMENT,
} from 'src/constants/constants'
import {
  IAlspSolutionDataFragment,
  IEnum_Componentsolutionpropsdedicatedresource_Type,
  ISimilarSolutionItemFragment,
} from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { AppState } from 'src/store/types'
import { CompareInfos } from 'src/store/types/compare'
import { DedicatedResourcesType, FeaturesType, SectionItem } from 'src/types'
import { checkNotEmpty } from 'src/utils/checkNotEmpty'
import { getFeaturesItems } from 'src/utils/getFeaturesItems'
import { getYearFoundedItem } from 'src/utils/getYearFoundedItem'
import { canViewPaywalledSolutionFields } from 'src/utils/solutionPaywall'

import IconBusiness from '../../../assets/icon_business.svg'
import IconLawFirms from '../../../assets/icon_law_firms.svg'

interface OverviewContainerProps {
  className?: string
  alsp: IAlspSolutionDataFragment
  similars: ISimilarSolutionItemFragment[]
  relatedContent?: any[]
  totalRelatedContent?: number
}

const CompareModalContent = dynamic(
  () => import('src/components/CompareModalContent/CompareModalContent').then((mod) => mod.CompareModalContent),
  {
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)
const Section = dynamic(() => import('src/components/global/Overview/Section').then((mod) => mod.Section), {
  loading: () => <SkeletonLoading className="bg-navy" />,
})
const ArticleCart = dynamic(() => import('src/components/ArticleCart/ArticleCart').then((mod) => mod.ArticleCart), {
  loading: () => <SkeletonLoading className="bg-navy" />,
})
const SimilarSolutions = dynamic(
  () => import('src/components/SimilarSolutions/SimilarSolutions').then((mod) => mod.SimilarSolutions),
  {
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)

/**
 * Primary UI component for user interaction
 */
export const OverviewContainer: React.FC<OverviewContainerProps> = memo(
  ({ className, alsp, similars, relatedContent = [], totalRelatedContent = 0 }) => {
    const enhancedListingData = alsp?.alspData?.enhancedListingData

    const url = alsp?.website || ''
    const productType = alsp?.productType?.toLocaleLowerCase() || ''
    const consolidationData: any = alsp?.consolidationData || null
    const hqs = alsp?.hqs || []
    const regionsServed = alsp?.regionsServed || []
    const languages = alsp?.languages || []
    const offices = alsp?.offices || []
    const features = alsp?.features || []
    const yearFounded = alsp?.yearFounded
    const audiences = alsp?.alspData?.audiences || []
    const serviceOfferings = alsp?.subTopics || []
    const existingCustomers = alsp?.existingCustomers || []
    const descOtherExistingCustomer = alsp?.descOtherExistingCustomer || ''
    const partners = alsp?.alspData?.partners || ''
    const size = alsp?.alspData?.size || 0
    const linkedin = alsp?.linkedin || ''
    const twitter = alsp?.twitter || ''
    const [isVisible, setIsVisible] = React.useState(false)
    const closeDrawer = React.useCallback(() => setIsVisible(false), [])
    const compareInfos: CompareInfos = useSelector((state: AppState) => state.compareInfos)
    const { session } = useAuthenticated()
    const confirmed = session?.user.confirmed || false
    const fundingInfo = alsp?.alspData?.fundingInfo
    const fundingAmount = alsp?.alspData?.fundingAmount
    const alspData: any = alsp?.alspData
    const founders = alsp?.founders || []
    const isDiverseLeadership: any = alsp?.isDiverseLeadership || false
    const fundingDetails = alsp?.alspData?.fundingDetails || null
    const openToInvestment = alsp?.alspData?.openToInvestment || false
    const companyType = session?.user?.companyType || ''
    const isPremiumUser = session?.user?.isPremium || false
    const unlockPaywalledContent = canViewPaywalledSolutionFields(!!session?.user?.isPremium, alsp, session?.user?.id)

    let showOpenToInvestment = false
    if (companyType.toLocaleLowerCase() === 'investor') {
      showOpenToInvestment = true
    }

    useEffect(() => {
      if (compareInfos?.solutions?.length > 0 && confirmed) {
        if (compareInfos.solutions.length === 1) {
          // const compare: CompareSolution = compareInfos.solutions[0]
          /* if (compare.slug !== alsp?.slug) {
                      setIsVisible(true)
                    } else {
                      setIsVisible(false)
                    } */
          setIsVisible(true)
        } else {
          setIsVisible(true)
        }
      } else {
        setIsVisible(false)
      }
    }, [compareInfos])

    const overviewItems: SectionItem[] = []
    if (checkNotEmpty(hqs)) {
      const convertedHqs: FeaturesType[] =
        hqs?.filter((item) => !!item?.name).map((item) => ({ name: item?.name || '' })) || []
      const item: SectionItem = { title: 'HEADQUARTERS', type: 'SpanDataIcon', subType: 'normal', items: convertedHqs }
      overviewItems.push(item)
    }
    if (checkNotEmpty(linkedin)) {
      const displayName = alsp?.displayName || 'Company'
      const title = `View ${displayName} on LinkedIn`
      const item: SectionItem = { type: 'LinkedinIconLink', items: [linkedin!], title }
      overviewItems.push(item)
    }
    if (checkNotEmpty(languages)) {
      const convertedLangs: FeaturesType[] =
        languages?.filter((item) => !!item?.name).map((item) => ({ name: item?.name || '' })) || []
      const item: SectionItem = { title: 'LANGUAGES', type: 'SpanDataList', subType: 'normal', items: convertedLangs }
      overviewItems.push(item)
    }
    if (checkNotEmpty(existingCustomers)) {
      const items: FeaturesType[] =
        existingCustomers
          ?.filter((item) => !!item?.name)
          .map((item) => ({
            name: item?.name || '',
            ...(item?.name.toLowerCase() === 'others' && { name: descOtherExistingCustomer }),
          })) || []
      const item: SectionItem = {
        title: 'EXISTING CUSTOMERS',
        type: 'SpanDataList',
        subType: 'normal',
        items,
      }
      overviewItems.push(item)
    }
    if (checkNotEmpty(offices)) {
      const convertedOffices: FeaturesType[] =
        offices?.filter((item) => !!item?.name).map((item) => ({ name: item?.name || '' })) || []
      const item: SectionItem = { title: 'OFFICES', type: 'SpanDataList', subType: 'normal', items: convertedOffices }
      overviewItems.push(item)
    }
    if (checkNotEmpty(twitter)) {
      const displayName = alsp?.displayName || 'Company'
      const title = `View ${displayName} on Twitter`
      const item: SectionItem = { type: 'TwitterIconLink', items: [twitter!], title }
      overviewItems.push(item)
    }
    if (checkNotEmpty(regionsServed)) {
      const convertedRegions: FeaturesType[] =
        regionsServed?.filter((item) => !!item?.name).map((item) => ({ name: item?.name || '' })) || []
      const item: SectionItem = {
        title: 'REGIONS SERVED',
        type: 'SpanDataList',
        subType: 'normal',
        items: convertedRegions,
      }
      overviewItems.push(item)
    }
    if (checkNotEmpty(serviceOfferings)) {
      const convertedItems: FeaturesType[] =
        serviceOfferings?.filter((item) => !!item?.name).map((item) => ({ name: item?.name || '' })) || []

      const item: SectionItem = {
        title: 'SERVICE OFFERINGS',
        type: 'DottedDataList',
        subType: '',
        items: convertedItems,
        parentGridCols: 1,
        childGridCols: 1,
      }
      overviewItems.push(item)
    }

    const serviceItems: SectionItem[] = []
    if (checkNotEmpty(audiences)) {
      const convertedItems: FeaturesType[] =
        (audiences &&
          audiences?.map((item) => {
            const name = item?.name || ''
            let icon = ''
            if (name === 'Law Firms') icon = IconLawFirms
            else if (name === 'Corporate Legal') icon = IconBusiness
            const converted: FeaturesType = { name: name, icon: icon }
            return converted
          })) ||
        []

      const item: SectionItem = { title: 'CUSTOMER TYPE', type: 'SpanDataList', subType: 'cart', items: convertedItems }
      serviceItems.push(item)
    }

    if (checkNotEmpty(enhancedListingData?.averageLengthOfEngagement)) {
      const engagementLen: number = enhancedListingData?.averageLengthOfEngagement || 0
      const item: SectionItem = {
        title: 'Average length of Engagement',
        type: 'number',
        subType: 'cart',
        items: [],
        nValue: engagementLen,
      }
      serviceItems.push(item)
    }
    if (checkNotEmpty(enhancedListingData?.dedicatedResources)) {
      const dedicatedResources: DedicatedResourcesType[] =
        (enhancedListingData?.dedicatedResources &&
          enhancedListingData?.dedicatedResources.map((resource) => {
            const item: DedicatedResourcesType = {
              count: resource?.count || 0,
              type: resource?.type
                ? resource?.type === IEnum_Componentsolutionpropsdedicatedresource_Type.FullTime
                  ? IEnum_Componentsolutionpropsdedicatedresource_Type.FullTime
                  : IEnum_Componentsolutionpropsdedicatedresource_Type.PartTime
                : IEnum_Componentsolutionpropsdedicatedresource_Type.FullTime,
            }
            return item
          })) ||
        []

      const item: SectionItem = {
        title: 'Dedicated Resources for Service Offering',
        type: 'dedicated',
        subType: 'cart',
        items: [],
        dValue: dedicatedResources || [],
      }
      serviceItems.push(item)
    }
    if (checkNotEmpty(enhancedListingData?.serviceDescription)) {
      const serviceDescription = enhancedListingData?.serviceDescription || ''

      const item: SectionItem = {
        title: 'Service Description',
        type: 'string',
        subType: 'cart',
        items: [],
        sValue: serviceDescription,
        isFullWidth: true,
      }
      serviceItems.push(item)
    }
    if (checkNotEmpty(alspData?.valueProposition)) {
      const item: SectionItem = {
        title: 'Value Proposition',
        type: 'DottedDataList',
        subType: 'more',
        items: alspData?.valueProposition ? [{ name: alspData?.valueProposition }] : [],
        sValue: alspData?.valueProposition || '',
        isFullWidth: true,
      }
      serviceItems.push(item)
    }
    /* if (checkNotEmpty(fundingAmount)) {
    const item: SectionItem = {
      title: 'Total Funding Amount',
      type: 'DottedDataList',
      subType: 'more',
      items: [{ name: fundingAmountsC.find((item: any) => item.value === fundingAmount)?.label }],
    }
    overviewItems.push(item)
  }
   */
    if (checkNotEmpty(enhancedListingData?.technologyPartners)) {
      const item: SectionItem = {
        title: 'Technology Partners',
        type: 'string',
        subType: 'cart',
        items: [],
        sValue: enhancedListingData?.technologyPartners || '',
        isFullWidth: true,
      }
      serviceItems.push(item)
    }

    const experItems: SectionItem[] = []
    if (checkNotEmpty(yearFounded)) {
      const yearFoundedItem = getYearFoundedItem('Operating Since', yearFounded)
      if (yearFoundedItem) {
        experItems.push(yearFoundedItem)
      }
    }
    if (checkNotEmpty(enhancedListingData?.specialists)) {
      const items: FeaturesType[] =
        enhancedListingData?.specialists
          ?.filter(
            (resource) =>
              resource?.name?.trim() !== '' && resource?.role?.trim() !== '' && resource?.bio?.trim() !== '',
          )
          .map((resource) => {
            const item: FeaturesType = { name: resource?.name || '' }
            return item
          }) || []
      if (items.length > 0) {
        const item: SectionItem = {
          title: 'Specialists',
          type: 'DottedDataList',
          subType: '',
          items: items,
        }
        experItems.push(item)
      }
    }
    if (checkNotEmpty(size)) {
      const item: SectionItem = {
        title: 'NUMBER OF CONSULTANTS',
        type: 'number',
        subType: '',
        items: [],
        nValue: size || 0,
      }
      experItems.push(item)
    }
    if (checkNotEmpty(partners)) {
      const item: SectionItem = {
        title: 'Partners',
        type: 'string',
        subType: '',
        items: [],
        sValue: partners || '',
        isFullWidth: true,
      }
      experItems.push(item)
    }
    if (checkNotEmpty(founders)) {
      const item: SectionItem = {
        title: FOUNDERS_EXECUTIVE_TEAM, // 'Founders/Executive Team',
        type: 'CustomList',
        subType: '',
        isFullWidth: true,
        items: founders || [],
        isDiverseLeadership: isDiverseLeadership,
      }
      experItems.push(item)
    }

    if (checkNotEmpty(fundingInfo)) {
      const item: SectionItem = {
        title: 'Latest Funding Round',
        type: 'DottedDataList',
        subType: 'more',
        items: [{ name: fundingInfosC.find((item: any) => item.value === fundingInfo)?.label }],
      }
      overviewItems.push(item)
    }

    if (checkNotEmpty(fundingAmount)) {
      const item: SectionItem = {
        title: 'Total Funding Amount',
        type: 'DottedDataList',
        subType: 'more',
        items: [{ name: fundingAmountsC.find((item: any) => item.value === fundingAmount)?.label }],
      }
      overviewItems.push(item)
    }
    if (THEME === 'legaltech' && checkNotEmpty(fundingDetails)) {
      const item: SectionItem = {
        title: FUNDING_DETAILS,
        type: 'string',
        subType: '',
        items: [],
        sValue: fundingDetails || '',
        isFullWidth: true,
      }
      overviewItems.push(item)
    }

    if (
      THEME === 'legaltech' &&
      openToInvestment &&
      ((showOpenToInvestment && isPremiumUser) || canViewPaywalledSolutionFields(false, alsp, session?.user?.id))
    ) {
      const item: SectionItem = {
        title: 'OPEN_TO_INVESTMENT',
        type: 'CheckDataList',
        isBold: true,
        isFullWidth: true,
        items: [{ name: OPEN_TO_INVESTMENT, isChecked: true }],
      }
      overviewItems.push(item)
    }

    const featuresItems: SectionItem[] = getFeaturesItems(
      serviceOfferings?.filter((item) => item != null) || [],
      features?.filter((item) => item != null) || [],
    )
    const randomNum = Math.floor(Math.random() * 1000) // Generates a number between 0 and 999

    const showWebsite =
      productType === 'graveyard' || (productType === 'consolidation' && consolidationData?.enableInActiveDir === false)
        ? false
        : true

    return (
      <div className={cn(className, 'flex flex-col justify-center w-full')}>
        {compareInfos.solutions && compareInfos.solutions.length ? (
          <Drawer duration={250} hideScrollbars={true} onClose={closeDrawer} isVisible={isVisible}>
            <CompareModalContent onClose={closeDrawer} solutionType={compareInfos.solutions[0].solutionType} />
          </Drawer>
        ) : null}

        <div className="flex flex-col justify-center">
          <div className="md:pl-0 pl-[1.25rem] pr-[1.25rem] !max-w-[1100px] w-full flex md:flex-row flex-col py-2">
            <div
              className={`flex flex-col ${similars.length > 0 || relatedContent?.length > 0 ? 'md:w-[50rem] md:mr-[2.3125rem]' : 'w-full'} w-full`}
            >
              <Section
                key={randomNum + '-Overview'}
                title={'Overview'}
                description={alsp?.longDescription || ''}
                url={!showWebsite ? '' : url || ''}
                items={overviewItems}
                unlockPaywalledContent={unlockPaywalledContent}
              />
              {featuresItems.length > 0 && (
                <Section
                  key={randomNum + '-Features'}
                  title={'Features'}
                  description={''}
                  url={''}
                  items={featuresItems}
                  unlockPaywalledContent={unlockPaywalledContent}
                />
              )}
              {serviceItems.length > 0 && (
                <Section
                  key={randomNum + '-Services'}
                  title={'Services'}
                  description={''}
                  url={''}
                  items={serviceItems}
                  unlockPaywalledContent={unlockPaywalledContent}
                />
              )}
              {experItems.length > 0 && (
                <Section
                  key={randomNum + '-Experience'}
                  title={'Experience'}
                  description={''}
                  url={''}
                  items={experItems}
                  renderItem={(founder) => (
                    <SolutionFounderItem key={randomNum + '-SolutionFounderItem'} founder={founder} />
                  )}
                  unlockPaywalledContent={unlockPaywalledContent}
                />
              )}
              {checkNotEmpty(alsp?.articles) && <ArticleCart articles={alsp?.articles || []} />}
            </div>

            {(similars.length > 0 || relatedContent?.length > 0) && (
              <div className="flex flex-col md:w-[15rem] w-full h-full mb-auto">
                <ThemeOnly theme="legaltech">
                  {relatedContent && (
                    <RelatedContents
                      contents={relatedContent}
                      slug={alsp?.slug ? 'alsps/' + alsp?.slug : ''}
                      totalRelatedContent={totalRelatedContent}
                    />
                  )}
                </ThemeOnly>

                {similars.length > 0 && (
                  <SimilarSolutions
                    key={randomNum + '-SimilarSolutions'}
                    similars={similars}
                    slug={alsp?.slug || ''}
                    originName={alsp?.slug || ''}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  },
)
